import React, { useMemo } from 'react';
import EChart from '../../components/Charts/Echart';

const numberFormat = new Intl.NumberFormat('es-AR', { maximumFractionDigits: 3 });

const usablePoints = output => output?.shape === 'series' && Array.isArray(output.points)
  ? output.points.map(point => ({
    timestamp: point.timestamp,
    value: point.value !== null && point.value !== '' && Number.isFinite(Number(point.value))
      ? Number(point.value)
      : null,
    quality: point.quality || 'good',
  }))
  : [];

export const comparisonChartData = (referenceOutput, evaluationOutput) => {
  const reference = usablePoints(referenceOutput);
  const evaluation = usablePoints(evaluationOutput);
  const length = Math.max(reference.length, evaluation.length);
  return Array.from({ length }, (_, index) => ({
    day: index + 1,
    reference: reference[index]?.value ?? null,
    evaluation: evaluation[index]?.value ?? null,
    referenceQuality: reference[index]?.quality,
    evaluationQuality: evaluation[index]?.quality,
  }));
};

export default function ProjectResultsChart({ referenceOutput, evaluationOutput, unit }) {
  const data = useMemo(
    () => comparisonChartData(referenceOutput, evaluationOutput),
    [evaluationOutput, referenceOutput],
  );

  if (referenceOutput?.shape !== 'series' || evaluationOutput?.shape !== 'series') {
    return <div className="project-results-chart-empty"><strong>Este indicador entrega un único valor por período.</strong><span>La comparación numérica es válida, pero no existe una serie temporal para dibujar una curva.</span></div>;
  }
  if (!data.length) {
    return <div className="project-results-chart-empty"><strong>No hay puntos suficientes para mostrar la evolución.</strong></div>;
  }

  const option = {
    animationDuration: 280,
    color: ['#73879c', '#1abb9c'],
    grid: { top: 54, right: 24, bottom: 42, left: 72 },
    legend: { top: 4, textStyle: { color: '#52677a', fontSize: 12 } },
    tooltip: {
      trigger: 'axis',
      valueFormatter: value => value == null ? 'Sin dato' : `${numberFormat.format(value)} ${unit || ''}`.trim(),
    },
    xAxis: {
      type: 'category', boundaryGap: false,
      data: data.map(point => `Día ${point.day}`),
      axisLabel: { color: '#52677a', hideOverlap: true },
      axisLine: { lineStyle: { color: '#ccd6df' } },
    },
    yAxis: {
      type: 'value',
      axisLabel: { color: '#52677a', formatter: value => numberFormat.format(value) },
      splitLine: { lineStyle: { color: '#e8edf1', type: 'dashed' } },
    },
    series: [
      { name: 'Referencia', type: 'line', showSymbol: false, connectNulls: false, lineStyle: { width: 3 }, data: data.map(point => point.reference) },
      { name: 'Período evaluado', type: 'line', showSymbol: false, connectNulls: false, lineStyle: { width: 3 }, data: data.map(point => point.evaluation) },
    ],
  };

  return <section className="project-results-chart" aria-label="Curvas históricas de referencia y período evaluado">
    <div><h4>Evolución del indicador</h4><p>Comparación por día equivalente dentro de cada período.</p></div>
    <EChart option={option} className="project-results-chart-canvas" />
    <small>Las líneas no unen días sin resultado válido.</small>
  </section>;
}
